import PropTypes from 'prop-types';
import React, { createRef, PureComponent } from 'react';
import { Observable } from 'rxjs';
import classnames from 'classnames';
import get from 'lodash/get';
import MaskedInput from 'react-text-mask';
import { withStyles } from '@glu/theming';
import { withValidation } from '@glu/validation-react';
import { ExclamationTriangleIcon } from '@glu/icons-react';
import withCharCounter from './withCharCounter';
import DebouncedInput from './DebouncedInput';

import uniqId from '../../utils/uniqId';

const checkboxTypes = ['checbox', 'radio'];
const styles = ({
  palette: {
    grey: { G600 }
  },
  spacing: { unit },
  typography: { fontFamily }
}) => ({
  warningMessage: {
    alignItems: 'flex-start',
    display: 'flex',
    fontFamily,
    fontSize: `${unit * 1.5}px`,
    margin: '10px 0 0'
  },
  optional: {
    color: G600,
    float: 'right',
    fontSize: `${unit * 1.5}px`,
    fontWeight: 'normal',
    textTransform: 'none',
    textAlign: 'right',
    lineHeight: `${unit * 3}px`
  },
  helpMsgClass: {
    textAlign: 'left'
  }
});
export class InputComponent extends PureComponent {
  static propTypes = {
    append: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    autoFocus: PropTypes.bool,
    dataQa: PropTypes.string,
    className: PropTypes.string,
    charCount: PropTypes.string,
    debounceInput: PropTypes.bool,
    disabled: PropTypes.bool,
    errors: PropTypes.shape({}),
    helpMsg: PropTypes.string,
    inputClass: PropTypes.string,
    inputComponent: PropTypes.shape({}),
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    labelClass: PropTypes.string,
    mask: PropTypes.oneOfType([
      PropTypes.shape({}),
      PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]))
    ]),
    name: PropTypes.string.isRequired,
    onBlur: PropTypes.func,
    onChange: PropTypes.func.isRequired,
    optional: PropTypes.bool,
    prepend: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    radioValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
    registerValidators: PropTypes.func.isRequired,
    rendersInput: PropTypes.bool,
    unregisterValidators: PropTypes.func.isRequired,
    registerSelfValidatingChild: PropTypes.func,
    unregisterSelfValidatingChild: PropTypes.func,
    validate: PropTypes.func,
    validateBlurOnly: PropTypes.bool,
    type: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
    onBlurValidations: PropTypes.arrayOf(
      PropTypes.shape({
        validate: PropTypes.func.isRequired,
        msg: PropTypes.string.isRequired
      })
    ),
    forwardedRef: PropTypes.shape({}),
    warningMessage: PropTypes.string,
    warningStyle: PropTypes.shape({}),
    messageContainerStyles: PropTypes.shape({})
  };

  static defaultProps = {
    append: null,
    autoFocus: false,
    className: 'form-group',
    charCount: '',
    debounceInput: false,
    disabled: false,
    dataQa: '',
    errors: {},
    helpMsg: '',
    inputClass: 'form-control',
    inputComponent: null,
    label: '',
    labelClass: 'control-label',
    mask: null,
    onBlur() {},
    onBlurValidations: [],
    optional: false,
    prepend: null,
    radioValue: '',
    registerSelfValidatingChild: null,
    rendersInput: false,
    type: 'text',
    unregisterSelfValidatingChild: null,
    validate: null,
    validateBlurOnly: false,
    value: '',
    forwardedRef: null,
    warningMessage: null,
    warningStyle: {},
    messageContainerStyles: {}
  };

  controlRef = createRef();

  state = { id: uniqId('input-'), onBlurValidationErrors: [] };

  componentDidMount() {
    const { autoFocus, forwardedRef } = this.props;
    if (autoFocus) {
      (forwardedRef || this.controlRef).current.focus();
    }
  }

  onChange = (event, gluValue) => {
    const { checked, type, value = gluValue } = event.target || event;
    const dataValue = checkboxTypes.includes(type) ? checked : value;
    this.props.onChange(this.props.name, dataValue, !this.props.validateBlurOnly);
  };

  onBlur = async (event, gluValue) => {
    const { checked, type, value = gluValue } = event.target || event;
    const dataValue = checkboxTypes.includes(type) ? checked : value;
    // third arg to onChange is validate flag for onBlur validation
    this.props.onChange(this.props.name, dataValue, true);
    this.props.onBlur(this.props.name, dataValue);

    // If there are any onBlur validations then run them
    // Example validation with service: { validate: ({ serviceData, name, value }) => data.isTrue ? true : false }, service: ({ name, value }) => observableService$, msg: 'Failed' }
    // The validate function will have access to the service data as well as the name and value of the field
    // TODO: find a way to hook into Glu's FormContextField and add errors there to maintain consistency
    if (this.props.onBlurValidations.length > 0) {
      const onBlurValidationErrors = [];
      const checkAndSetErrors = ({ isValid, onBlurValidationErrors, validation }) => {
        if (!isValid) onBlurValidationErrors.push(validation.msg);
        if (onBlurValidationErrors.length > 0)
          this.setState((state) => ({ ...state, onBlurValidationErrors }));
        else this.setState((state) => ({ ...state, onBlurValidationErrors: [] }));
      };
      for (let i = 0; i < this.props.onBlurValidations.length; i += 1) {
        const validation = this.props.onBlurValidations[i];
        if (validation.service) {
          const service$ = validation.service({ name: this.props.name, value: dataValue });
          const canSubscribe = service$ instanceof Observable;

          if (!canSubscribe) {
            return;
          }

          service$.subscribe((data) => {
            const isValid = validation.validate({
              name: this.props.name,
              value: dataValue,
              serviceData: data
            });
            checkAndSetErrors({ isValid, validation, onBlurValidationErrors });
          });
        } else {
          const isValid = validation.validate({ name: this.props.name, value: dataValue });
          checkAndSetErrors({ isValid, validation, onBlurValidationErrors });
        }
      }
    }
  };

  render() {
    const { id, onBlurValidationErrors } = this.state;
    const {
      append,
      className,
      classes,
      disabled,
      errors,
      helpMsg,
      inputClass,
      inputComponent,
      label,
      labelClass,
      mask,
      messageContainerStyles,
      name,
      optional,
      prepend,
      radioValue,
      rendersInput,
      type,
      value,
      warningMessage,
      warningStyle
    } = this.props;

    const errorMsg = get(errors, name);
    // NOTE:
    const errorMsgCheck = Array.isArray(errorMsg) ? errorMsg.length > 0 : Boolean(errorMsg);
    const errorClass = {
      'has-error': errorMsgCheck || onBlurValidationErrors.length > 0
    };

    const inputFirst = checkboxTypes.includes(type);
    const checkedInput = type === 'checkbox' ? { checked: value } : null;
    const valueProp =
      checkedInput || type === 'radio'
        ? { checked: value === radioValue, value: radioValue }
        : { value: value ?? '' };

    const labelElem = label ? (
      <label className={labelClass} htmlFor={id}>
        {label}
      </label>
    ) : null;

    // Need to remove helpMsg to prevent console error
    // Need to convert optional to string
    const {
      charCount,
      debounceInput,
      forwardedRef,
      helpMsg: removeHelp,
      inputClass: removeInput,
      inputComponent: removeInputComponent,
      labelClass: removeLabel,
      onBlurValidations,
      optional: changeOptional,
      // Remove to prevent errors
      radioValue: removeRadio,
      registerSelfValidatingChild,
      registerValidators,
      rendersInput: removeRendersInput,
      unregisterSelfValidatingChild,
      unregisterValidators,
      validate,
      validateBlurOnly,
      ...other
    } = this.props;

    const inputProps = {
      ...other,
      id,
      type,
      name,
      ...valueProp,
      onChange: this.onChange,
      onBlur: this.onBlur,
      // TODO: Fix errors caused by this ref
      // See CurrencyInput for an example
      ...(!debounceInput && { ref: forwardedRef || this.controlRef }),
      ...(debounceInput && { debounceInput }),
      // ref: forwardedRef || this.controlRef,
      className: inputClass,
      optional: `${!!changeOptional}`,

      data: undefined,
      errors: undefined,
      validators: undefined
    };

    let InputComponent = 'input';

    if (inputComponent) {
      InputComponent = inputComponent;
    }

    if (mask) {
      InputComponent = MaskedInput;
    }

    if (debounceInput && !inputComponent) {
      InputComponent = DebouncedInput;
    }

    let fullProps;
    let maxlengthProp;

    // mask and CurrencyInput render <input> elements
    if (inputComponent && !rendersInput && !mask) {
      fullProps = inputProps;
    } else {
      // Remove props that will cause problem for an <input> component.
      // throws this error:
      // React does not recognize the `resetErrors` prop on a DOM element.
      // If you intentionally want it to appear in the DOM as a custom attribute, spell it as lowercase `reseterrors` instead.
      // If you accidentally passed it from a parent component, remove it from the DOM element.
      const {
        dataQa,
        debounceInput,
        maxlength,
        messageContainerStyles,
        resetData,
        resetErrors,
        warningMessage,
        warningStyle,
        ...rest
      } = inputProps;
      maxlengthProp = maxlength;
      fullProps = rest;
    }

    const addon = prepend || append;

    // get remaining and max characters for input
    let remaining;
    let max;
    if (charCount) {
      const [enteredCount, maxCount] = charCount.split('/');
      remaining = maxCount - enteredCount;
      max = maxCount;
    }

    return (
      <div className={classnames(className, errorClass)}>
        {!inputFirst && labelElem}
        {optional && <span className={classes.optional}>Optional</span>}
        {!addon && (
          <InputComponent
            disabled={disabled}
            {...(maxlengthProp && { maxLength: maxlengthProp })}
            {...fullProps}
            mask={mask}
          />
        )}
        {addon && (
          <div className="input-group">
            {prepend}
            <InputComponent
              disabled={disabled}
              {...(maxlengthProp && { maxLength: maxlengthProp })}
              {...fullProps}
              mask={mask}
            />
            {append}
          </div>
        )}

        {Boolean(inputFirst) && labelElem}
        <div
          data-qa="help-block-container"
          style={{ display: 'flex', justifyContent: 'space-between', ...messageContainerStyles }}
        >
          <div>
            {errorMsg && <span className="help-block">{errorMsg}</span>}
            {onBlurValidationErrors.length > 0 && (
              <span className="help-block">
                {onBlurValidationErrors.map((message) => (
                  <p key={message}>{message}</p>
                ))}
              </span>
            )}
            {!errorMsg && !onBlurValidationErrors.length > 0 && helpMsg && (
              <span className={`help-block ${classes.helpMsgClass}`}>{helpMsg}</span>
            )}
          </div>
          {charCount && (
            <span className="help-block">
              {charCount}
              <span aria-live="polite" className="sr-only">
                {remaining >= 1
                  ? `${remaining} of ${max} characters remaining`
                  : `Character limit reached`}
              </span>
            </span>
          )}
          {/* {isSameDay && invalidAmount && balanceCheckType === 1 && ( */}
          {warningMessage && (
            <p className={classes.warningMessage} style={warningStyle}>
              <ExclamationTriangleIcon
                style={{ marginRight: '5px', width: '20px', height: '20px', fill: '#df0000' }}
              />
              {warningMessage}
            </p>
          )}
        </div>
      </div>
    );
  }
}

const Styled = withStyles(styles)(InputComponent);
const Validated = withValidation(Styled);
export const InputWithCharCounter = withCharCounter(Validated);
export default Validated;
